/**
 * Account management (see UsersPanel) and the access audit log, admin tier only.
 *
 * Filterable by who acted, who was acted on, and what was done, because those are the three
 * questions actually asked of it: "what has this staff member been looking at", "who has been
 * looking at this player", and "who has played clips this week".
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAudit } from "../api";
import { formatTimestamp } from "../format";
import { UsersPanel } from "./UsersPanel";
import { AppUploadPanel } from "./AppUploadPanel";

const ACTIONS = [
  "clip_playback",
  "alt_lookup",
  "ip_reveal",
  "voice_list",
  "login",
  "login_failed",
  "logout",
  "password_changed",
  "user_created",
  "user_password_reset",
  "user_deleted",
  "user_tier_changed",
  "login_alert_rejected",
  "app_unlock_password_failed",
  "app_unlock_password_ok",
  "login_alert_confirmed",
  "session_signed_out",
  "signed_out_everywhere",
  "app_uploaded",
  "app_removed",
  "access_denied",
];

export function AdminTab({
  onOpenPlayer,
  csrfToken,
  currentUserId,
}: {
  onOpenPlayer: (player: string) => void;
  csrfToken: string;
  currentUserId: string;
}): JSX.Element {
  const [action, setAction] = useState("");
  const [target, setTarget] = useState("");

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (action) params.set("action", action);
    if (target) params.set("target", target);
    return params.toString();
  }, [action, target]);

  const audit = useQuery({
    queryKey: ["audit", query],
    queryFn: () => fetchAudit(query, null),
    // The audit log is not on the live stream — nothing about it needs to be sub-second, and putting
    // staff members' own activity on a push feed invites watching each other in real time.
    refetchInterval: 30_000,
  });

  return (
    <>
      <AppUploadPanel csrfToken={csrfToken} />

      <UsersPanel csrfToken={csrfToken} currentUserId={currentUserId} />

      <h2 className="section-title">Access audit</h2>
      <div className="filterbar">
        <select
          className="input"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          aria-label="Filter by action"
        >
          <option value="">All actions</option>
          {ACTIONS.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="Target player"
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          aria-label="Filter by target player"
          style={{ width: "18ch" }}
        />
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Staff member</th>
              <th>Action</th>
              <th>Target</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(audit.data?.entries.length ?? 0) === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  {audit.isLoading ? "Loading…" : "Nothing recorded yet."}
                </td>
              </tr>
            )}
            {audit.data?.entries.map((entry) => (
              <tr key={entry.id}>
                <td className="mono">{formatTimestamp(entry.occurred_at)}</td>
                <td>
                  {entry.username ?? "unknown"}
                  <div className="mono" style={{ fontSize: 10.5 }}>
                    {entry.user_id}
                  </div>
                </td>
                <td className="mono">{entry.action}</td>
                <td>
                  {entry.target ? (
                    <button
                      type="button"
                      className="who"
                      onClick={() => onOpenPlayer(entry.target as string)}
                    >
                      {entry.target}
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="mono" style={{ whiteSpace: "normal" }}>
                  {entry.detail ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
